import React from 'react';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import * as XLSX from 'xlsx-js-style';
import { Download } from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';

export interface TableRow {
  MIS_Description: string;
  Total: number;
  Average: number;
  Apr: number;
  May: number;
  Jun: number;
  Jul: number;
  Aug: number;
  Sep: number;
  Oct: number;
  Nov: number;
  Dec: number;
  Jan: number;
  Feb: number;
  Mar: number;
}

export interface CustomerDataRow {
  customer_code: string;
  customer_name: string;
  metric?: string | null;
  row_kind: 'header' | 'data' | 'metric';
  Total: number;
  Average: number;
  Apr: number;
  May: number;
  Jun: number;
  Jul: number;
  Aug: number;
  Sep: number;
  Oct: number;
  Nov: number;
  Dec: number;
  Jan: number;
  Feb: number;
  Mar: number;
}

interface DataTableProps {
  data: TableRow[];
  customerData?: CustomerDataRow[];
  showCustomerView?: boolean;
  loading: boolean;
  conversionType: 'INR' | 'Lakhs' | 'Crores';
  onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
  showCustomerViewButton?: boolean;
  onCustomerView?: () => void;
  showDownloadButton?: boolean;
  dataType?: string; // 'PnL' or 'ABP' for filename generation
  hasLoadedOnce?: boolean;
  showBulkDownloadButton?: boolean;
  onBulkDownload?: () => void;
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  customerData = [],
  showCustomerView = false,
  loading,
  conversionType,
  onConversionChange,
  showCustomerViewButton,
  onCustomerView,
  showDownloadButton,
  dataType = 'Data',
  hasLoadedOnce,
  showBulkDownloadButton,
  onBulkDownload
}) => {
  const { isLoading, showContent } = useSmoothLoading(loading, {
    minLoadingTime: 150,
    debounceTime: 150
  });

  const [displayMode, setDisplayMode] = React.useState<'values' | 'percentage'>('values');

  const formatNumber = (value: number, isPercent = false): string => {
    if (value === 0) return isPercent ? '0.00%' : '0.00';

    let displayValue = value;
    if (!isPercent) {
      switch (conversionType) {
        case 'Lakhs':
          displayValue = value / 100000;
          break;
        case 'Crores':
          displayValue = value / 10000000;
          break;
        case 'INR':
        default:
          displayValue = value;
          break;
      }
    }

    const formatted = displayValue.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return isPercent ? `${formatted}%` : formatted;
  };

  const isPercentRow = (description: string): boolean => {
    return description.includes('%');
  };

  const getColumnPercentage = (rowValue: number, baseValue: number, isAlreadyPercentRow: boolean): string => {
    if (displayMode === 'values') {
      return formatNumber(rowValue, isAlreadyPercentRow);
    }

    if (isAlreadyPercentRow) {
      return '';
    }

    if (!baseValue) return '0.00%';

    const pct = (rowValue / baseValue) * 100;
    return formatNumber(pct, true);
  };

  const totalRevenueRow = React.useMemo(() => {
    return data.find(row =>
      row.MIS_Description.toLowerCase().includes('total revenue')
    );
  }, [data]);

  const handleDownloadExcel = () => {
    const excelData: any[] = [];

    const headers = [
      'MIS Description', 'Total', 'Average', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'
    ];
    excelData.push(headers);

    if (showCustomerView && customerData.length > 0) {
      // Export customer data
      customerData.forEach((row) => {
        if (row.row_kind === 'header') {
          excelData.push([row.customer_name.trim(), '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
        } else {
          const isPercent = row.metric?.includes('%') || false;
          excelData.push([
            row.metric,
            formatNumber(row.Total, isPercent),
            formatNumber(row.Average, isPercent),
            formatNumber(row.Apr, isPercent),
            formatNumber(row.May, isPercent),
            formatNumber(row.Jun, isPercent),
            formatNumber(row.Jul, isPercent),
            formatNumber(row.Aug, isPercent),
            formatNumber(row.Sep, isPercent),
            formatNumber(row.Oct, isPercent),
            formatNumber(row.Nov, isPercent),
            formatNumber(row.Dec, isPercent),
            formatNumber(row.Jan, isPercent),
            formatNumber(row.Feb, isPercent),
            formatNumber(row.Mar, isPercent)
          ]);
        }
      });
    } else {
      // Export main data
      data.forEach((row) => {
        const isPercent = isPercentRow(row.MIS_Description);
        excelData.push([
          row.MIS_Description,
          getColumnPercentage(row.Total, totalRevenueRow?.Total || 0, isPercent),
          getColumnPercentage(row.Average, totalRevenueRow?.Average || 0, isPercent),
          getColumnPercentage(row.Apr, totalRevenueRow?.Apr || 0, isPercent),
          getColumnPercentage(row.May, totalRevenueRow?.May || 0, isPercent),
          getColumnPercentage(row.Jun, totalRevenueRow?.Jun || 0, isPercent),
          getColumnPercentage(row.Jul, totalRevenueRow?.Jul || 0, isPercent),
          getColumnPercentage(row.Aug, totalRevenueRow?.Aug || 0, isPercent),
          getColumnPercentage(row.Sep, totalRevenueRow?.Sep || 0, isPercent),
          getColumnPercentage(row.Oct, totalRevenueRow?.Oct || 0, isPercent),
          getColumnPercentage(row.Nov, totalRevenueRow?.Nov || 0, isPercent),
          getColumnPercentage(row.Dec, totalRevenueRow?.Dec || 0, isPercent),
          getColumnPercentage(row.Jan, totalRevenueRow?.Jan || 0, isPercent),
          getColumnPercentage(row.Feb, totalRevenueRow?.Feb || 0, isPercent),
          getColumnPercentage(row.Mar, totalRevenueRow?.Mar || 0, isPercent),
        ]);
      });
    }

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    const totalRows = excelData.length;
    const totalCols = headers.length;

    worksheet['!cols'] = [
      { wch: 30 },
      ...Array(14).fill({ wch: 15 })
    ];

    worksheet['!rows'] = Array.from({ length: totalRows }).map((_, i) =>
      i === 0 ? { hpt: 24 } : { hpt: 16 }
    );

    for (let r = 0; r < totalRows; r++) {
      for (let c = 0; c < totalCols; c++) {
        const cellAddress = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[cellAddress]) continue;

        const isHeader = r === 0;
        const isFirstCol = c === 0;

        worksheet[cellAddress].s = {
          font: {
            bold: isHeader || isFirstCol,
            color: { rgb: "000000" }
          },
          fill: isHeader
            ? {
              patternType: "solid",
              fgColor: { rgb: "D9D9D9" }
            }
            : undefined,
          alignment: {
            vertical: "center",
            horizontal: isHeader ? "center" : "left"
          },
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" }
          }
        };
      }
    }
    worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };

    const sheetName = showCustomerView ? 'Customer Data' : `${dataType} Data`;
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const filename = showCustomerView
      ? `Customer_Data_${conversionType}_${timestamp}.xlsx`
      : `${dataType}_Data_${conversionType}_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  const opacityClass =
    isLoading || showContent ? 'opacity-100' : 'opacity-0';

  const renderLoadingRows = () => {
    const rows = 19;
    const cols = 15;

    return Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={`skeleton-row-${rowIndex}`} className="hover:bg-gray-50">
        {Array.from({ length: cols }).map((_, colIndex) => (
          <td
            key={`skeleton-cell-${rowIndex}-${colIndex}`}
            className="border border-gray-200 px-2 py-1.5"
          >
            <Skeleton
              className={`h-4 ${colIndex === 0 ? 'w-3/4' : 'w-full'}`}
            />
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div className={`mt-6 space-y-2 transition-opacity duration-300 ${opacityClass}`}>
      {/* Controls Row */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === 'INR'
              ? 'bg-blue-600 text-white'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            onClick={() => onConversionChange('INR')}
          >
            INR
          </button>
          <button
            className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === 'Lakhs'
              ? 'bg-blue-600 text-white'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            onClick={() => onConversionChange('Lakhs')}
          >
            Lakhs
          </button>
          <button
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${conversionType === 'Crores'
              ? 'bg-blue-600 text-white'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            onClick={() => onConversionChange('Crores')}
          >
            Crores
          </button>
        </div>
        <div className="flex gap-2">
          <>
            <div className="flex items-center bg-gray-100 rounded-lg p-1 mr-3">
              <button
                className={`px-3 py-1 text-sm rounded-md transition-all duration-150 font-medium ${displayMode === 'values'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => setDisplayMode('values')}
              >
                Values
              </button>
              <button
                className={`px-3 py-1 text-sm rounded-md transition-all duration-150 font-medium ${displayMode === 'percentage'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => setDisplayMode('percentage')}
              >
                Percentage
              </button>
            </div>

            {showDownloadButton && (
              <button
                className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1"
                onClick={handleDownloadExcel}
              >
                <Download className="w-4 h-4" />
                Excel
              </button>
            )}

            {showBulkDownloadButton && (
              <button
                className="px-3 py-1 text-sm rounded-md border border-purple-600 text-purple-700 hover:bg-purple-50 flex items-center gap-1"
                onClick={onBulkDownload}
              >
                <Download className="w-4 h-4" />
                Bulk Excel
              </button>
            )}
          </>
          {showCustomerViewButton && (
            <button
              className="px-3 py-1 text-sm rounded-md border border-gray-500 text-black hover:bg-gray-100 transition-colors"
              onClick={onCustomerView || (() => { })}
            >
              {showCustomerView ? 'Hide Customer Level Data' : 'View Customer Level Data'}
            </button>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div className="w-full overflow-x-auto border border-gray-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-2 py-2 text-left text-gray-700 font-bold min-w-[150px]">
                MIS Description
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Total
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Average
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Apr
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                May
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Jun
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Jul
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Aug
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Sep
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Oct
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Nov
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Dec
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Jan
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Feb
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                Mar
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderLoadingRows()
            ) : showCustomerView ? (
              customerData.map((row, index) => {
                if (row.row_kind === 'header') {
                  return (
                    <tr
                      key={`${row.customer_code}-header-${index}`}
                      className="bg-gray-100 font-bold"
                    >
                      <td className="border border-gray-200 px-2 py-1.5 text-left font-bold" colSpan={15}>
                        {row.customer_name.trim()}
                      </td>
                    </tr>
                  );
                } else {
                  const isPercent = row.metric?.includes('%') || false;
                  return (
                    <tr
                      key={`${row.customer_code}-${row.metric}-${index}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="border border-gray-200 px-2 py-1.5 text-left font-bold pl-4">
                        {row.metric}
                      </td>
                      <td className={`border border-gray-200 px-2 py-1.5 text-right font-bold ${isPercent ? 'bg-gray-100' : 'bg-blue-50'}`}>
                        {formatNumber(row.Total, isPercent)}
                      </td>
                      <td className={`border border-gray-200 px-2 py-1.5 text-right font-bold ${isPercent ? 'bg-gray-100' : 'bg-blue-50'}`}>
                        {formatNumber(row.Average, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Apr, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.May, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Jun, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Jul, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Aug, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Sep, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Oct, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Nov, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Dec, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Jan, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Feb, isPercent)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right">
                        {formatNumber(row.Mar, isPercent)}
                      </td>
                    </tr>
                  );
                }
              })
            ) : (
              data.map((row, index) => {
                const percent = isPercentRow(row.MIS_Description);

                return (
                  <tr
                    key={index}
                    className={`hover:bg-gray-50 ${percent ? 'bg-gray-100' : ''
                      }`}
                  >
                    <td className="border border-gray-200 px-2 py-1.5 text-left font-bold">
                      {row.MIS_Description}
                    </td>
                    <td className={`border border-gray-200 px-2 py-1.5 text-right font-bold ${percent ? 'bg-gray-100' : 'bg-blue-50'}`}>
                      {getColumnPercentage(row.Total, totalRevenueRow?.Total || 0, percent)}
                    </td>
                    <td className={`border border-gray-200 px-2 py-1.5 text-right font-bold ${percent ? 'bg-gray-100' : 'bg-blue-50'}`}>
                      {getColumnPercentage(row.Average, totalRevenueRow?.Average || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Apr, totalRevenueRow?.Apr || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.May, totalRevenueRow?.May || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Jun, totalRevenueRow?.Jun || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Jul, totalRevenueRow?.Jul || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Aug, totalRevenueRow?.Aug || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Sep, totalRevenueRow?.Sep || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Oct, totalRevenueRow?.Oct || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Nov, totalRevenueRow?.Nov || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Dec, totalRevenueRow?.Dec || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Jan, totalRevenueRow?.Jan || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Feb, totalRevenueRow?.Feb || 0, percent)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {getColumnPercentage(row.Mar, totalRevenueRow?.Mar || 0, percent)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {hasLoadedOnce && !loading && !isLoading && (showCustomerView ? customerData.length === 0 : data.length === 0) && (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {showCustomerView ? 'No customer data available.' : 'No data available. Please select filters to view data.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTable;
